// iter78s — Printable location sign dialog.
// Mirrors PrintLabelDialog / WorkOrderLabelDialog UX:
//   • Same STICKER_SIZES picker (1×1 → 4×6)
//   • Live preview
//   • Remembers last size per device
// Sign content (auto-fit per size):
//   • Bin type colored band (Receiving / Storage / WIP / FinishedGoods)
//   • Location name (big), bin ID (mono)
//   • Placement / parent breadcrumb (when room allows)
//   • QR code linking to /locations/view/{id} (so staff can scan and see what's there)

import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Printer } from 'lucide-react';
import { STICKER_SIZES } from './PrintLabelDialog';

const STORAGE_KEY = 'loc:sign_size_id';

const BIN_TYPE_TONE = {
  Receiving: { bg: '#dbeafe', band: '#2563eb', text: '#1e3a8a', label: 'RECEIVING' },
  Storage: { bg: '#fef9c3', band: '#ca8a04', text: '#713f12', label: 'STORAGE' },
  WIP: { bg: '#fed7aa', band: '#ea580c', text: '#7c2d12', label: 'WIP' },
  FinishedGoods: { bg: '#dcfce7', band: '#16a34a', text: '#14532d', label: 'FINISHED GOODS' },
};
const tone = (t) => BIN_TYPE_TONE[t] || { bg: '#f1f5f9', band: '#64748b', text: '#0f172a', label: (t || 'LOCATION').toUpperCase() };

const LocationSignDialog = ({ open, onOpenChange, location }) => {
  const [sizeId, setSizeId] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) || '4x6'; }
    catch { return '4x6'; }
  });
  const [copies, setCopies] = useState(1);
  useEffect(() => { try { window.localStorage.setItem(STORAGE_KEY, sizeId); } catch {} }, [sizeId]);

  const size = useMemo(
    () => STICKER_SIZES.find(s => s.id === sizeId) || STICKER_SIZES.find(s => s.id === '4x6') || STICKER_SIZES[0],
    [sizeId]
  );
  const isLandscape = size.width >= size.height * 1.3;
  const t = tone(location?.bin_type);

  if (!location) return null;

  const qrTarget = `${window.location.origin}/locations/view/${location.id}`;
  const veryTiny = size.width <= 2 && size.height <= 1;
  const small = size.width <= 2.625 && size.height <= 1.1;
  const placementLine = !small ? (location.placement || '') : '';

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=600,height=600');
    if (!w) {
      window.alert('Please allow pop-ups to print signs.');
      return;
    }
    const pad = Math.max(0.04, Math.min(0.10, Math.min(size.width, size.height) * 0.08));
    const innerW = Math.max(0.1, size.width - pad * 2);
    const innerH = Math.max(0.1, size.height - pad * 2);
    const baseQrIn = isLandscape
      ? Math.min(innerH * 0.78, innerW * 0.45)
      : Math.min(innerW * 0.65, innerH * 0.45);
    const qrPx = Math.max(36, Math.round(baseQrIn * 96));
    const qrSvg = renderToStaticMarkup(
      <QRCodeSVG value={qrTarget} size={qrPx} level="M" includeMargin={false} />
    );
    const bandH = Math.max(0.16, Math.min(0.32, size.height * 0.16));
    const nameSize = isLandscape ? Math.max(14, innerH * 26) : Math.max(14, innerW * 14);
    const binSize = isLandscape ? Math.max(9, innerH * 14) : Math.max(9, innerW * 8);
    const placeSize = Math.max(7, innerW * 5);
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const labelHtml = `
      <div class="sign" style="background:${t.bg};color:${t.text}">
        <div class="band" style="background:${t.band}">${esc(t.label)}</div>
        <div class="body ${isLandscape ? 'row' : 'col'}">
          <div class="qr">${qrSvg}</div>
          <div class="text">
            <div class="name">${esc(location.location_name || '')}</div>
            <div class="bin">${esc(location.bin_id || '')}</div>
            ${placementLine ? `<div class="place">${esc(placementLine)}</div>` : ''}
            ${veryTiny ? '' : `<div class="hint">Scan for item list</div>`}
          </div>
        </div>
      </div>`;
    const copiesN = Math.max(1, parseInt(copies, 10) || 1);
    const all = Array.from({ length: copiesN }, () => labelHtml).join('');
    w.document.write(`<!doctype html><html><head><title>Location Sign · ${esc(location.bin_id || '')}</title>
      <style>
        @page { size: ${size.width}in ${size.height}in; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        .sign {
          width: ${size.width}in; height: ${size.height}in; page-break-after: always;
          display: flex; flex-direction: column; overflow: hidden;
        }
        .band {
          color: white; font-weight: 800; font-size: ${Math.max(7, bandH * 36)}pt;
          letter-spacing: 0.18em; text-align: center; padding: ${bandH * 0.18}in 0;
          flex-shrink: 0;
        }
        .body { flex: 1; padding: ${pad}in; display: flex; gap: ${Math.max(0.04, pad)}in; min-height: 0; }
        .body.row { flex-direction: row; align-items: center; text-align: left; }
        .body.col { flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .qr { flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .qr svg { width: ${baseQrIn}in; height: ${baseQrIn}in; display: block; }
        .text { flex: 1; min-width: 0; overflow: hidden; line-height: 1.05; }
        .name { font-weight: 900; font-size: ${nameSize}pt; word-break: break-word; }
        .bin { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 700; font-size: ${binSize}pt; margin-top: 0.04in; opacity: 0.85; }
        .place { font-size: ${placeSize}pt; margin-top: 0.04in; opacity: 0.7; font-style: italic; }
        .hint { font-size: ${Math.max(6, placeSize * 0.85)}pt; margin-top: 0.06in; opacity: 0.6; letter-spacing: 0.05em; text-transform: uppercase; }
      </style></head><body>
      ${all}
      <script>window.onload=function(){window.focus();window.print();window.onafterprint=function(){window.close();};};</script>
      </body></html>`);
    w.document.close();
  };

  // Preview
  const PREVIEW_PX = 60;
  const pW = size.width * PREVIEW_PX;
  const pH = size.height * PREVIEW_PX;
  const previewQr = isLandscape ? Math.floor(pH * 0.55) : Math.floor(pW * 0.5);
  const showHint = !veryTiny;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="loc-sign-dialog">
        <DialogHeader>
          <DialogTitle>Print Location Sign · {location.bin_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Sign Size</Label>
            <Select value={sizeId} onValueChange={setSizeId}>
              <SelectTrigger className="rounded-sm" data-testid="loc-sign-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STICKER_SIZES.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`loc-sign-size-option-${s.id}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500">Last-used size is remembered on this device.</p>
          </div>

          <div className="space-y-2">
            <Label>Copies</Label>
            <Input type="number" min={1} max={50} value={copies} onChange={(e) => setCopies(e.target.value)} className="rounded-sm w-24 mono" data-testid="loc-sign-copies-input" />
          </div>

          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex justify-center">
              <div
                className="border border-dashed border-slate-300 flex flex-col overflow-hidden"
                style={{ width: pW, height: pH, background: t.bg, color: t.text }}
                data-testid="loc-sign-preview"
              >
                <div style={{ background: t.band, color: 'white', textAlign: 'center', fontWeight: 800, letterSpacing: '0.18em', padding: '2px 0', fontSize: Math.min(14, Math.max(6, pH * 0.07)) }}>
                  {t.label}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: isLandscape ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 6, textAlign: isLandscape ? 'left' : 'center' }}>
                  <QRCodeSVG value={qrTarget} size={previewQr} level="M" includeMargin={false} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: isLandscape ? 18 : 14, lineHeight: 1.05, wordBreak: 'break-word' }}>{location.location_name}</div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: isLandscape ? 12 : 11, opacity: 0.85 }}>{location.bin_id}</div>
                    {!small && location.placement && <div style={{ fontSize: 9, fontStyle: 'italic', opacity: 0.7, marginTop: 2 }}>{location.placement}</div>}
                    {showHint && <div style={{ fontSize: 8, marginTop: 4, opacity: 0.55, letterSpacing: 0.05, textTransform: 'uppercase' }}>Scan for item list</div>}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">Page size: {size.width}″ × {size.height}″</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handlePrint} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="loc-sign-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print {parseInt(copies, 10) > 1 ? `${parseInt(copies, 10)} signs` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationSignDialog;
